import axios from "axios";
import { clearAuthHint } from "./authHint";

/**
 * Backend URL resolution (handles custom-domain deploys without rebuild).
 *
 * Rule: if the page is loaded on a host DIFFERENT from REACT_APP_BACKEND_URL
 * (e.g. user moved from `foo.emergent.host` → `pickleague.vn`), always
 * fall back to same-origin to avoid cross-origin CORS+credentials blocks.
 */
function resolveBackendUrl() {
  const envUrl = process.env.REACT_APP_BACKEND_URL || "";
  if (!envUrl) return "";  // Empty env → same-origin (relative `/api`)
  if (typeof window === "undefined") return envUrl;
  try {
    const envHost = new URL(envUrl).host;
    const pageHost = window.location.host;
    if (envHost && pageHost && envHost !== pageHost) {
      // Custom-domain mismatch detected → same-origin fallback.
      return "";
    }
    return envUrl;
  } catch {
    return "";
  }
}

const BACKEND_URL = resolveBackendUrl();
export { BACKEND_URL };
export const API = `${BACKEND_URL}/api`;

// Phase 85.A.206 — WebSocket variant. KEEP same smart-fallback logic
// như BACKEND_URL: nếu host mismatch (custom domain → preview env),
// fallback về "" → callers tự dùng `window.location.host`. KHÔNG được
// dùng `process.env.REACT_APP_BACKEND_URL` trực tiếp ở các WS hooks vì
// process.env không có fallback → khi build production có env cũ
// (auto-seeding-engine.emergent.host) trong khi user truy cập
// pickleague.vn, WS sẽ trỏ về host đã chết → 520 + connection failed.
export const BACKEND_WS_URL = BACKEND_URL ? BACKEND_URL.replace(/^http/, "ws") : "";

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,   // send/receive httpOnly auth cookies
});

// ── Đợt Offline-1 (06/2026) — Mutation guard khi CONFIRMED offline ─────────
// KHÔNG chặn theo HTTP method mù: phân loại bằng offlinePolicy tường minh
// (config.offlinePolicy) hoặc default theo namespace. HARD ALLOWLIST nhỏ cho
// endpoint vận hành: referee/publisher/healthz — connection-lock trọng tài và
// watchdog publisher TOÀN QUYỀN, guard này không được can thiệp.
// Policies: block (toast, không gửi) | drop (bỏ im lặng) | ops-managed (đi
// qua) | critical (đi qua — auth/session tự xử lý lỗi) | allow.
const _OPS_URL_RE = /\/referee\/|\/publisher\/|\/healthz|\/livestream\/self\/sessions\/[^/]+\/(start|stop|control)/;
function _offlinePolicyFor(config) {
  if (config.offlinePolicy) return config.offlinePolicy;
  const u = config.url || "";
  if (_OPS_URL_RE.test(u)) return "ops-managed";
  if (/\/auth\//.test(u)) return "critical";
  if (/\/public\/visit|viewer\/heartbeat|\/analytics|\/perf/.test(u)) return "drop";
  return "block";
}
let _lastOfflineToastAt = 0;
api.interceptors.request.use((config) => {
  const method = (config.method || "GET").toUpperCase();
  if (method === "GET") return config;
  let gov;
  try { gov = require("./networkGovernor"); } catch { return config; }
  if (!gov.isOffline() || gov.isPassiveContext()) return config;
  const policy = _offlinePolicyFor(config);
  if (policy === "ops-managed" || policy === "critical" || policy === "allow") return config;
  const err = new axios.Cancel(`offline_${policy}`);
  err.isOfflineBlocked = policy === "block";
  err.isOfflineDrop = policy === "drop";
  if (policy === "block" && Date.now() - _lastOfflineToastAt > 5000) {
    _lastOfflineToastAt = Date.now();
    try {
      const { toast } = require("sonner");
      toast.error("Thao tác chưa được gửi. Cần kết nối Internet để thực hiện.");
    } catch { /* noop */ }
  }
  return Promise.reject(err);
});

// Phase 88.E.18 — Client-side ETag cache cho admin list endpoints "nặng".
// Vì K8s ingress/Cloudflare override `Cache-Control: no-store, no-cache,
// must-revalidate`, browser KHÔNG store response → axios không tự gửi
// If-None-Match. Ta tự quản 1 Map trong RAM: lưu `{etag, body}` keyed
// theo (method+url+params), tự thêm `If-None-Match`, và khi BE trả 304
// thì axios sẽ resolve với body cached. TTL 5s — đủ để chống reload spam
// nhưng vẫn fresh cho live tournament.
const _ETAG_CACHE = new Map(); // key → { etag, body, expiry }
const _ETAG_TTL_MS = 5000;
// Whitelist regex (chỉ apply cho endpoint đã có server-side ETag — tránh
// case load 304 "rỗng" rồi serve sai body cho endpoint khác).
const _ETAG_URL_RE = /\/admin\/tournaments\/[^/]+\/(matches\/(history|picker)|registrations(\/.*)?(\?.*)?$|events\/[^/]+\/registrations)|(^|\/)public\//;

function _cacheKey(config) {
  const method = (config.method || "GET").toUpperCase();
  const params = config.params ? JSON.stringify(config.params) : "";
  return `${method} ${config.url}|${params}`;
}

api.interceptors.request.use((config) => {
  const method = (config.method || "GET").toUpperCase();
  if (method === "GET" && _ETAG_URL_RE.test(config.url || "")) {
    const key = _cacheKey(config);
    const hit = _ETAG_CACHE.get(key);
    if (hit && hit.expiry > Date.now() && hit.etag) {
      config.headers = config.headers || {};
      config.headers["If-None-Match"] = hit.etag;
      config.__etagCacheKey = key;
      // Cho phép 304 đi qua "success path" thay vì reject.
      const prev = config.validateStatus;
      config.validateStatus = (s) => s === 304 || (prev ? prev(s) : s >= 200 && s < 300);
    } else {
      config.__etagCacheKey = key; // vẫn lưu key để response interceptor cache
    }
  }
  return config;
});

api.interceptors.response.use((res) => {
  const key = res.config?.__etagCacheKey;
  if (!key) return res;
  if (res.status === 304) {
    const hit = _ETAG_CACHE.get(key);
    if (hit) {
      // Refresh TTL để session admin còn dùng tiếp.
      hit.expiry = Date.now() + _ETAG_TTL_MS;
      // Trả body cached như success 200.
      return { ...res, status: 200, data: hit.body, __fromEtagCache: true };
    }
    return res;
  }
  const etag = res.headers?.etag || res.headers?.ETag;
  if (etag) {
    _ETAG_CACHE.set(key, {
      etag,
      body: res.data,
      expiry: Date.now() + _ETAG_TTL_MS,
    });
    // Cap tổng số entry để không grow unbounded (admin browse nhiều giải).
    if (_ETAG_CACHE.size > 64) {
      const firstKey = _ETAG_CACHE.keys().next().value;
      if (firstKey) _ETAG_CACHE.delete(firstKey);
    }
  }
  return res;
});

// Phase 85.A.351 — Khi gửi FormData (upload ảnh/file multipart), KHÔNG ép
// Content-Type JSON: để browser tự set `multipart/form-data; boundary=...`,
// nếu không FastAPI sẽ trả 422 vì không parse được multipart.
api.interceptors.request.use((config) => {
  if (typeof FormData !== "undefined" && config.data instanceof FormData) {
    if (config.headers) {
      delete config.headers["Content-Type"];
      delete config.headers["content-type"];
    }
  }
  // Phase 88.D — Auto-generate Idempotency-Key cho referee mutations (rally,
  // start, complete, forfeit, timeout, undo, score). Nếu FE đã set sẵn key
  // (vd retry queue) thì giữ nguyên — chỉ inject khi chưa có.
  try {
    const url = (config.url || "");
    const method = (config.method || "GET").toUpperCase();
    const isRefereeMut = method !== "GET" && /\/referee\/matches\/[^/]+\//.test(url);
    if (isRefereeMut && config.headers && !config.headers["Idempotency-Key"]) {
      const uuid = (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : `idem-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
      config.headers["Idempotency-Key"] = uuid;
    }
  } catch { /* never break request */ }
  return config;
});

// Phase 88.D — Retry interceptor cho referee-write requests bị 502/503/504
// hoặc network error (backend cycle / cold-start). Tối đa 3 lần, expo
// backoff 0.8s / 1.6s / 3.2s. Idempotency-Key đã được inject ở request
// interceptor → BE dedupe an toàn. KHÔNG retry POST /auth/login (FE
// muốn fail-fast feedback).
api.interceptors.response.use(undefined, async (err) => {
  const cfg = err.config || {};
  const status = err.response?.status;
  const url = cfg.url || "";
  const method = (cfg.method || "GET").toUpperCase();
  const retryable = (
    method !== "GET" &&
    /\/referee\/matches\/[^/]+\//.test(url) &&
    (status === 502 || status === 503 || status === 504 || (!err.response && err.code !== "ERR_CANCELED"))
  );
  if (!retryable) return Promise.reject(err);
  cfg.__retryCount = (cfg.__retryCount || 0) + 1;
  if (cfg.__retryCount > 3) return Promise.reject(err);
  const delay = 800 * Math.pow(2, cfg.__retryCount - 1);
  await new Promise((r) => setTimeout(r, delay));
  return api(cfg);
});

// 2026-07 stress-fix — Backend load-shed trả 503 {shed:true} khi quá tải.
// CHỈ auto-retry GET (idempotent, polling) tối đa 2 lần theo Retry-After +
// jitter 0-1s (tránh retry đồng loạt). POST KHÔNG auto-retry (tránh retry
// storm khuếch đại đúng lúc quá tải) — trừ referee mutations đã có
// idempotency-key + retry riêng ở interceptor trên.
api.interceptors.response.use(undefined, async (err) => {
  const cfg = err.config || {};
  const method = (cfg.method || "GET").toUpperCase();
  const isShed = err.response?.status === 503 && err.response?.data?.shed === true;
  if (!isShed || method !== "GET") return Promise.reject(err);
  cfg.__shedRetry = (cfg.__shedRetry || 0) + 1;
  if (cfg.__shedRetry > 2) return Promise.reject(err);
  const retryAfter = Number(err.response?.headers?.["retry-after"]) || 2;
  await new Promise((r) => setTimeout(r, retryAfter * 1000 + Math.random() * 1000));
  return api(cfg);
});

/**
 * Phase 62 — Auto-refresh access token on 401 (single-flight).
 *
 * Keeps the user logged-in seamlessly. When backend returns 401, we hit
 * `/auth/refresh` once (queueing parallel requests so we don't fire N
 * duplicates), then retry the original request. Routes excluded:
 *   - /auth/login, /auth/refresh, /auth/logout (avoid loops)
 */
let refreshPromise = null;
// v16.98 — HAR PROD: guest (không có refresh cookie) bị loop /auth/refresh
// 401 nhiều lần mỗi page-load (mỗi endpoint authed trả 401 lại kích một
// refresh mới sau khi refreshPromise reset). Fix: refresh trả 401/403
// (mất phiên THẬT / guest) → sessionDead=true, KHÔNG thử refresh lại cho
// tới khi login/register/me thành công.
let sessionDead = false;

api.interceptors.response.use(
  (res) => {
    if (sessionDead) {
      const u = res.config?.url || "";
      if (u.includes("/auth/login") || u.includes("/auth/register") || u.includes("/auth/me")) {
        sessionDead = false;
      }
    }
    return res;
  },
  async (err) => {
    const original = err.config || {};
    const status = err.response?.status;
    const url = original.url || "";

    // Phase 84.M.36 — Auto-record API errors vào error buffer kèm context
    // chi tiết (status, url, method, response detail, request size). Tránh
    // 401/403 spam — chỉ ghi 4xx (trừ 401/403) và 5xx.
    if (status && status !== 401 && status !== 403 && status >= 400) {
      try {
        // Lazy import để tránh circular dep (errorReporter import api).
        const { recordError } = await import("./errorReporter");
        const detail = err?.response?.data?.detail;
        const detailText = typeof detail === "string"
          ? detail
          : detail
            ? (() => { try { return JSON.stringify(detail); } catch { return String(detail); } })()
            : "";
        const reqBodySize = (() => {
          try {
            const b = original.data;
            if (!b) return 0;
            if (typeof b === "string") return b.length;
            return JSON.stringify(b).length;
          } catch { return 0; }
        })();
        recordError(detailText || `HTTP ${status} ${url}`, {
          source: "api",
          status,
          api_url: url,
          api_method: (original.method || "GET").toUpperCase(),
          response_detail: detailText,
          request_body_bytes: reqBodySize,
          response_status_text: err?.response?.statusText || "",
        });
      } catch {
        /* never let the reporter itself break the response chain */
      }
    }

    const isAuthRoute =
      url.includes("/auth/login") ||
      url.includes("/auth/refresh") ||
      url.includes("/auth/logout");

    // Phase 85.A.319 — Ghi nhận 401/403 trên endpoint referee vào trail chẩn
    // đoán (giúp truy nguyên nhân "trọng tài bị văng" — auth/session vs back).
    if ((status === 401 || status === 403) && url.includes("/referee")) {
      import("./refereeNavDiag")
        .then((m) => m.recordDiagEvent(status === 401 ? "http_401" : "http_403", { url }))
        .catch(() => {});
    }

    // Only intercept 401 once per request.
    // P0.3 (GĐ2 audit) — request đánh dấu __noRefresh (probe /auth/me của
    // guest chưa từng có phiên) → KHÔNG kích /auth/refresh.
    if (status !== 401 || original._retry || isAuthRoute || original.__noRefresh) {
      return Promise.reject(err);
    }
    // v16.98 — phiên đã chết (guest) → không spam refresh cho mỗi 401.
    if (sessionDead) {
      return Promise.reject(err);
    }
    original._retry = true;

    try {
      // Single-flight: collapse parallel refreshes into one request.
      if (!refreshPromise) {
        refreshPromise = (async () => {
          // Phase 88.E.33 — Retry refresh khi gặp transient error (502/503/
          // 504 / network) — thường xảy ra lúc BE redeploy. KHÔNG được
          // bắn `auth:expired` cho transient error (không phải mất phiên
          // thật) → tránh kick trọng tài ra Dashboard khi server restart.
          // Tối đa 5 lần, expo backoff 0.8 → 1.6 → 3.2 → 6.4 → 12.8 = ~25s.
          // 401/403 từ refresh → fail-fast (phiên hết hạn THẬT).
          const maxRetries = 5;
          let lastErr = null;
          for (let i = 0; i <= maxRetries; i++) {
            try {
              return await api.post("/auth/refresh");
            } catch (refreshErr) {
              const rStatus = refreshErr?.response?.status;
              const isTransient =
                rStatus === 502 ||
                rStatus === 503 ||
                rStatus === 504 ||
                (!refreshErr.response && refreshErr.code !== "ERR_CANCELED");
              if (!isTransient || i === maxRetries) {
                // v17 Đợt 1 — set sessionDead NGAY TRONG promise (trước khi
                // .finally() reset refreshPromise): đóng khe microtask từng
                // cho phép một 401 khác khởi động refresh THỨ HAI (thấy 2×
                // /auth/refresh mỗi page-load guest ở test iteration_454).
                if (rStatus === 401 || rStatus === 403) {
                  sessionDead = true;
                  clearAuthHint(); // PR-B: guest/mất phiên → skip auth ở load sau
                }
                lastErr = refreshErr;
                throw refreshErr;
              }
              const delay = 800 * Math.pow(2, i);
              // eslint-disable-next-line no-await-in-loop
              await new Promise((r) => setTimeout(r, delay));
            }
          }
          throw lastErr || new Error("refresh exhausted");
        })().finally(() => { refreshPromise = null; });
      }
      await refreshPromise;
      // Phase 85.A.319 — refresh thành công (phiên vẫn sống) → ghi trail.
      import("./refereeNavDiag")
        .then((m) => m.recordDiagEvent("token_refresh_ok", { url }))
        .catch(() => {});
    } catch (refreshFailure) {
      // v16.98 — 401/403 từ refresh = KHÔNG có phiên → chặn các lần sau.
      const rs = refreshFailure?.response?.status;
      if (rs === 401 || rs === 403) {
        sessionDead = true;
        clearAuthHint(); // PR-B
      }
      // Phase 85.A.319 — refresh THẤT BẠI → đây mới là tín hiệu mất phiên thật.
      import("./refereeNavDiag")
        .then((m) => m.recordDiagEvent("auth_refresh_failed", { url }))
        .catch(() => {});
      // Refresh failed → user must re-login.
      // Avoid hard redirect loops on the login page itself.
      if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
        // Allow auth context to handle session expiry gracefully.
        window.dispatchEvent(new CustomEvent("auth:expired"));
      }
      return Promise.reject(err);
    }

    return api(original);
  },
);

export const listTournaments = () => api.get("/tournaments").then((r) => r.data);
export const getTournament = (id) => api.get(`/tournaments/${id}`).then((r) => r.data);
export const createTournament = (body) => api.post("/tournaments", body).then((r) => r.data);
export const updateTournament = (id, body) => api.put(`/tournaments/${id}`, body).then((r) => r.data);
export const deleteTournament = (id) => api.delete(`/tournaments/${id}`).then((r) => r.data);
export const computeTournament = (body) => api.post("/tournaments/compute", body).then((r) => r.data);
export const updateResult = (id, results) => api.put(`/tournaments/${id}`, { results }).then((r) => r.data);
export const importTournament = (body) => api.post("/tournaments/import", body).then((r) => r.data);
